import React, {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import {__} from 'js/tool/page/current';
import SignatureCanvas from 'react-signature-canvas';
import ToggleButton from 'js/reusable/toggleButtonOld';
import {dateDisplayFormat, dateTimeSecondsDisplayFormat, yearDisplayFormat} from 'js/config/date/formats';
import {generateContractHTML, emptyImg} from 'js/application/components/dataModal/helper';
import Uploader from 'rsuite/lib/Uploader';
import dayjs from 'dayjs';
import scrollIntoView from 'scroll-into-view-if-needed';
import RepresentativeProofUploader from 'js/application/components/dataModal/representativeProofUploader';

function Agreement({
	handler,
	contractData,
	goBack,
	ip,
	isSaving,
}) {
	const variants                          = Object.keys(contractData.contractsHtml);
	const [selectedVariant, selectVariant]  = useState(variants[0]);
	const agreeRef                          = useRef(null);
	const sigDivRef                         = useRef(null);
	const sigRef                            = useRef(null);
	const docRef                            = useRef(null);
	const isRepRef                          = useRef(null);
	const [generatedHTML, setGeneratedHTML] = useState('');
	const [currTime, setCurrTime]           = useState(dayjs().format(dateTimeSecondsDisplayFormat));
	const [fileList, setFileList]           = useState([]);

	useEffect(() => {
		const timeTimeout = setInterval(() => {
			setCurrTime(dayjs().format(dateTimeSecondsDisplayFormat));
		}, 1000);

		return () => {
			clearTimeout(timeTimeout);
			clearSignature();
		};
	}, []);

	// Scroll to the top toggle box if it's not checked
	useEffect(() => {
		if (handler.validationErrors.proofFile) {
			scrollIntoView(isRepRef.current, {block: 'end', behavior: 'smooth', scrollMode: 'if-needed'});

			return;
		}

		if (handler.validationErrors.didReadTerms) {
			scrollIntoView(agreeRef.current, {block: 'end', behavior: 'smooth', scrollMode: 'if-needed'});

			return;
		}

		if (handler.validationErrors.signature) {
			scrollIntoView(sigDivRef.current, {block: 'end', behavior: 'smooth', scrollMode: 'if-needed'});
		}
	}, [
		handler.validationErrors.proofFile,
		handler.validationErrors.didReadTerms,
		handler.validationErrors.signature,
	]);

	useEffect(() => {
		const signature = handler.values.signature;

		setGeneratedHTML(generateContractHTML(
			contractData.contractsHtml[selectedVariant],
			{
				'%COMPANY_REP_NAME%':        contractData.companyRepName,
				'%COMPANY_REP_SURNAME%':     contractData.companyRepSurname,
				'%COMPANY_REP_EMAIL%':       contractData.companyRepEmail,
				'%COMPANY_REP_TITLE%':       contractData.companyRepTitle,
				'%COMPANY_CONTACT_NAME%':    contractData.companyContactName,
				'%COMPANY_CONTACT_SURNAME%': contractData.companyRepSurname,
				'%COMPANY_CONTACT_EMAIL%':   contractData.companyContactEmail,
				'%REG_NUM%':                 contractData.regNumber,
				'%TAX_NUM%':                 contractData.taxNumber,
				'%COMPANY_ADDRESS%':         contractData.companyAddress,
				'%COMPANY_NAME%':            contractData.companyName,
				'%BANK_NAME%':               contractData.bankName,
				'%BANK_ACCOUNT_NUMBER%':     contractData.bankAccountNumber,
				'%YEAR%':                    dayjs().format(yearDisplayFormat),
				'%TODAY_DATE%':              dayjs().format(dateDisplayFormat),
				'%NEXT_YEAR_DATE%':          dayjs().add(1, 'Y').format(dateDisplayFormat),
				'%SIGNATURE%':               signature ? signature : emptyImg,
				'%BORDER_STYLE%':            'border-bottom:1px solid #7d7d7d;',
			}
		));
	}, [contractData, handler.values.signature, selectedVariant]);

	function clearSignature() {
		if (sigRef && sigRef.current) {
			sigRef.current.clear();
		}

		handler.changeValue('signature')({target: {value: null}});
	}

	function signatureChanged() {
		handler.changeValue('signature')({target: {value: sigRef.current.toDataURL()}});
	}

	function fixIFrameHeight() {
		if (docRef) {
			docRef.current.style.height =
				(docRef.current.contentWindow.document.body.scrollHeight + 35) + 'px';
		}
	}

	function setSelectedVariant(variant) {
		return () => selectVariant(variant);
	}

	function getFileBase64(file, callback) {
		const reader = new FileReader();

		reader.onloadend = () => {
			callback(reader.result);
		};

		reader.readAsDataURL(file);
	}

	function setFileListSingle(files) {
		setFileList([files[files.length - 1]]);
	}

	function uploadImage(file) {
		if (!file || !file.blobFile || !file.blobFile.type || file.blobFile.type.split('/')[0] !== 'image') {
			return false;
		}

		getFileBase64(file.blobFile, value => {
			setImageToCanvas(value);
		});

		return false;
	}

	function setImageToCanvas(base64Code) {
		const image = new Image();

		image.onload = () => {
			const canvas = sigRef.current._canvas;

			const iWidth     = ((canvas.height * image.width) / image.height) - 20;
			const iHeight    = canvas.height - 20;
			const leftOffset = (canvas.width - iWidth) / 2;

			sigRef.current._sigPad._ctx.drawImage(image, leftOffset, 10, iWidth, iHeight);
			signatureChanged();
		};

		image.src = base64Code;
	}

	return (
		<Row
			className="acc-creation-row"
			style={{marginBottom: 0}}
		>
			<Col>
				<Card body>
					<Form.Row>
						<Col className="agreement-info">
							{__('fb:agreement must be signed by legal rep')}
						</Col>
					</Form.Row>
					<Row>
						<Col ref={isRepRef}>
							<RepresentativeProofUploader
								goBack={goBack}
								setProofFile={handler.changeValue('proofFile')}
								setValidationErrors={handler.setValidationErrors}
								validationError={handler.validationErrors.proofFile}
							/>
						</Col>
					</Row>

					<Row className="edit-form-group">
						<Col>
							<Form.Label className="edit-label">&nbsp;</Form.Label>

							<div className="variant-picker">
								{
									variants.map(variant => (
										<span
											key={variant}
											className={'variant-opt' + (variant === selectedVariant ? ' selected' : '')}
											onClick={setSelectedVariant(variant)}
										>
											{variant}
										</span>
									))
								}
							</div>

							<div className="terms-container">
								<iframe
									srcDoc={generatedHTML}
									scrolling="no"
									ref={docRef}
									onLoad={fixIFrameHeight}
								/>
							</div>
						</Col>
					</Row>

					<Row>
						<Col
							className="agreement-info agreement-check"
							ref={agreeRef}
						>
							<ToggleButton
								onToggle={handler.changeValue('didReadTerms')}
								checked={handler.values.didReadTerms}
								className="terms-agree"
								disabled={isSaving}
							/>

							<div className="rep-switch-text">
								{__('fb:i read and i agreee to terms')}
							</div>

							<Form.Control.Feedback
								type="invalid"
								style={{display: 'block'}}
							>
								{handler.validationErrors.didReadTerms}
							</Form.Control.Feedback>
						</Col>
					</Row>

					<Row className="edit-form-group mb-15">
						<Col ref={sigDivRef}>
							<Form.Label className="edit-label">
								<div>{__('fb:use trackpad to sign')}</div>
							</Form.Label>

							<div style={{position: 'relative'}}>
								<Uploader
									shouldUpload={uploadImage}
									fileListVisible={false}
									fileList={fileList}
									onChange={setFileListSingle}
									className="signature-uploader"
								>
									<Button
										size="sm"
										disabled={isSaving}
									>
										{__('fb:upload')} <i className="fas fa-upload"/>
									</Button>
								</Uploader>

								<SignatureCanvas
									ref={sigRef}
									penColor="#002d5f"
									canvasProps={{
										width:     612,
										height:    150,
										className: 'signature-canvas' +
											(handler.validationErrors.signature ? ' invalid' : ''),
									}
									}
									onEnd={signatureChanged}
								/>

								<span className="signature-line"/>

								<Button
									onClick={clearSignature}
									className="clear-signature-btn"
									disabled={isSaving}
								>
									{__('fb:clear signature')}&nbsp;<i className="fas fa-eraser"/>
								</Button>
							</div>

							<span className="device-info">
								{__('fb:date')}:&nbsp;
								<strong>{currTime}</strong>,&nbsp;
								{__('fb:ip')}:&nbsp;
								<strong>{ip}</strong>
							</span>

							<Form.Control.Feedback
								type="invalid"
								style={{display: 'block'}}
							>
								{handler.validationErrors.signature}
							</Form.Control.Feedback>
						</Col>
					</Row>

					<Row>
						<Col style={{fontSize: 14}}>
							{__('fb:you will receive a copy')}
						</Col>
					</Row>
				</Card>
			</Col>
		</Row>
	);
}

Agreement.propTypes = {
	handler:      PropTypes.instanceOf(Object).isRequired,
	contractData: PropTypes.instanceOf(Object).isRequired,
	goBack:       PropTypes.func,
	ip:           PropTypes.string.isRequired,
	isSaving:     PropTypes.bool,
};

export default Agreement;
